import { GsFormIfOperator } from 'libs/shared/src/components';
import { DotNetType, MapDotNetType } from 'libs/shared/src/models/basic/dotnet';
import { GsStructHorizontalAlignment } from '../component';

/** The possible types for the value of an input field */
export type InputBaseValueTypes = string | number | boolean;

/**
 * The value of a translation field (InputType.TranslationTextBox, backend `ComplexTranslation`):
 * one text per shop language, keyed by its ISO code (e.g. `{ "de-DE": "Beet vorne", "en-GB": "" }`).
 */
export type IComplexTranslationValue = Record<string, string>;

export interface IInputRender<ValueType = IInputOption<InputBaseValueTypes> | InputBaseValueTypes> {
  /** The alignment of the input-field left/center/right */
  alignment: GsStructHorizontalAlignment;
  /** Should auto completion get used by the browser? */
  autoComplete: boolean;
  /** Can the user clear the field with an dedicated clear-button */
  canClear: boolean;
  /** The constraints/validations of this input-field to check for */
  constraints: Array<IInputConstraint>;
  /** The unit label shown alongside the field value (e.g. "cm", "Minuten"). Not related to file extensions. */
  extension: string;
  /** The accept attribute for FileUpload inputs (e.g. "image/*") */
  accept?: string;
  /** The maximum size in bytes a file selected in a FileUpload input may have. 0/undefined means no limit. */
  maxFileSize?: number;
  /** The grouping-name for this input field */
  group: string;
  /** The height of the input-field in pixels */
  height: number;
  /** A hint for the user for some important informations */
  hint: string;
  /** The index of the field TODO: Describe */
  index: number;
  /** The type of input to display */
  inputType: InputType;
  /** Is the input shown with multiple lines? */
  multiLine: boolean;
  /** The name of the input field */
  name: string;
  /** Is the input-field nullable? */
  nullable: boolean;
  /** The order of the input field to sort by */
  order: number;
  /** The placeholder for the input field */
  placeHolder?: string;
  /** Is the input-field readonly? */
  readonly: boolean;
  /** Is the input-field required for submission? */
  required: boolean;
  /** The row to use (maby shared row with another input field) */
  row: string;
  /** The amount of rows for a text input field, for multiline inputs */
  rows?: number;
  /** TODO: Describe */
  stateClass: string;
  /** the tabIndex of this input-field */
  tabIndex: number;
  /** TODO: Describe */
  text: string;
  /** Title of the input, filled by alerts */
  title?: string;
  /** Source of the html to display inside the form (currently just "HtmlBox") */
  source: string;
  /** Type of Html-Input */
  textBoxInputType: TextBoxInputType;
  /** The tooltip to give while hovering on the input-field */
  toolTip: string;
  /** The current/initial value of the input-field */
  value: ValueType | ValueType[] | null;
  /** All possible values of the input-field */
  values: IInputOption[];
  /** The type of the value (Full DotNet type name) */
  valueType: string;
  /** Is the input a multiselect? */
  multiSelect: boolean;
  /** Can the order of a multiselect be sorted by the user? The order gets saved */
  sortable?: boolean;
  /** The width of the input-field */
  width: IInputWidth;
  /** Should this input get returned a formData-Attribute to the backend */
  isInput: boolean;
  /** All options for the select/dropdown etc. */
  select: IInputDialogOptions;
  /** Html attributes added to the input field */
  htmlAttributes?: {
    /** Style attribute added to the input field */
    style: string;
  };
  /** If-Attribute to check if the input should get displayed to the UI */
  isVisible?: IInputIf;
  /** If-Attribute to check if the input should be enabled (resulting in false means disabled) */
  updateEnabled: IInputIf;
  /** The currently implemented custom-inpt-field type */
  customType: CustomInputType;
  /** An individual title for the dialog */
  customTitle: string;
  /** A caption for the input */
  caption: string;
  /** The tabSheet this input belongs to */
  tabSheet?: IInputTab;
}

/** Interface for a tab in the input form */
export interface IInputTab {
  /** Name of the tab to identify the tab */
  name: string;
  /** The caption of the tab to display on the tab-header */
  caption: string;
  /** The glyph to display on the tab-header */
  glyph: string;
  /** Should the tab be loaded lazily (content only gets loaded when the user clicks on the tab for the first time) */
  lazyLoading: boolean;
}

/** Interface for the if-conditions to check if an input should be visible/enabled */
export interface IInputIf {
  /** The arguments to check for */
  args: IInputIfArgument[];
  /** The chaining of the arguments */
  chaining: IInputIfChaining;
}

/** Enum for the chaining of the if-arguments */
export enum IInputIfChaining {
  AND = 0,
  OR = 1
}

/** Interface for an argument in the if-conditions */
export interface IInputIfArgument {
  /** The formControlName/name of the input-field to check */
  name: string;
  /** The operator to check for */
  operator: GsFormIfOperator;
  /** The value to check for */
  value: boolean | string | number | null | Array<string | number>;
}

/** Interface for an option in the input field */
export interface IInputOption<T = string> {
  /** Text to display to the user (readable) */
  text: string;
  /** Value to send to the backend **/
  value: T | null;
}

/** Interface for a input constraint */
export interface IInputConstraint {
  /** Maximum number for number fields */
  max: number;
  /** Minimum number for number fields */
  min: number;
  /** Max length of a string */
  maxLength: number;
  /** Min length of a string */
  minLength: number;
  /** The message to display if failing constraint */
  message: string;
  /** The name of the constraint */
  name: string;
  /** The regex to check for the field */
  regex?: IInputConstraintRegex;
  /** The type of constraint */
  type: IInputConstraintType;
  /** The name of the other input field to compare against (for repeat/confirmation constraints) */
  otherInputField?: string;
  /** Minimum date for date range constraints (serialized as ISO string by the backend) */
  minDate?: Date;
  /** Maximum date for date range constraints (serialized as ISO string by the backend) */
  maxDate?: Date;
  /** The names of the inputs sharing an `InputConstraintBlock` — at least one of them must be filled */
  blockFields?: string[];
}

/** Interface for the regex constraint of an input field */
export interface IInputConstraintRegex {
  // The regex pattern to check (double backslashes because of string-format)
  pattern: string;
  // The options for the regex
  options: number;
}

export interface IInputDialogOptions {
  dialog: {
    /** Title of the modal */
    title: string;
    /** Subtitle of the modal */
    subTitle: string;
    /** Text of the confirm button */
    okButtonText: string;
    /** Text of the abort button */
    abortButtonText: string;
    /** The type of the items to select */
    name: IInputSelectType;
    /** The options of the possible selectable items */
    action?: IRenderAction;
  };
}

export interface IRenderAction {
  /** The url of the api-endpoint to load the items */
  url: string;
  /** Parameters for the url as a dictionary/object */
  routeValues: IRouteValue[];
}

/** All possible types of items wich can get provided by the backend for the object-select (e.g.: evaluate item-templates) */
export type IInputSelectType = 'SelectCategoryDialog' | 'SelectArticleGroupDialog' | 'SelectSearchableTypesDialog' | 'SelectTagDialog';

/**
 * All the different types of input constraints
 */
export type IInputConstraintType =
  | 'InputConstraintRequired'
  | 'InputConstraintMaxLength'
  | 'InputConstraintMinLength'
  | 'InputConstraintEMail'
  | 'InputConstraintOnlyLetters'
  | 'InputConstraintContainsValidZIP'
  | 'InputConstraintLengthMinMax'
  | 'InputConstraintMin'
  | 'InputConstraintMax'
  | 'InputConstraintPassword'
  | 'InputConstraintPasswordMismatch'
  | 'InputConstraintRegex'
  | 'InputConstraintRepeat'
  | 'InputConstraintDateRange'
  | 'InputConstraintBlock';

/**
 * Enum for the different input types used by the input render
 */
export enum InputType {
  TextBox = 0,
  CheckBox = 1,
  ComboBox = 2,
  RadioGroup = 3,
  DropDown = 4,
  Label = 5,
  ListView = 6,
  Separator = 7,
  Captcha = 8,
  ReCaptcha = 9,
  Headline = 10,
  Optional = 11,
  Hidden = 12,
  CRUDListView = 13,
  Image = 14,
  FileUpload = 15,
  Custom = 16,
  TranslationTextBox = 17,
  Button = 18, // Please use a normal Button instead to link it to an action
  ProgressBar = 19,
  Link = 20,
  Spacer = 21,
  HtmlBox = 22, // Do not just render HTML
  Table = 23,
  TabControl = 24,
  ImageAreaSelect = 25,
  ContentBuilder = 26,
  Break = 27
}

/** The type for the currently implemented custom-fields  */
export type CustomInputType = 'FormBuilder' | 'Alert' | 'FileUpload';

/**
 * Enum for the different textbox input types used in the input field component
 */
export enum TextBoxInputType {
  Text = 0,
  Password = 1,
  Datetime = 2,
  Datetime_local = 3,
  Date = 4,
  Month = 5,
  Time = 6,
  Week = 7,
  Number = 8,
  Email = 9,
  Url = 10,
  Search = 11,
  Tel = 12,
  Color = 13,
  Html = 14,
  Markup = 15,
  Fax = 16,
  TextOrEmail = 17
}

/** All possible types a text-input can have: https://www.w3schools.com/html/html_form_input_types.asp */
export type TextInputType =
  | 'button'
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'image'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'reset'
  | 'search'
  | 'submit'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

/** All possible inputmode values for HTML inputs: https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode */
export type InputMode = 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';

/** A mapping of the backend textbox-input-types to the real html ones */
export const TEXT_INPUT_TYPES: Record<TextBoxInputType, TextInputType> = {
  0: 'text',
  1: 'password',
  2: 'date',
  3: 'datetime-local',
  4: 'date',
  5: 'month',
  6: 'time',
  7: 'week',
  8: 'number',
  9: 'email',
  10: 'url',
  11: 'search',
  12: 'tel',
  13: 'color',
  14: 'text',
  15: 'text',
  16: 'tel',
  17: 'text'
};

/**
 * A mapping of the backend textbox-input-types to HTML inputmode attribute for better mobile keyboard support.
 * The inputmode attribute provides a hint to browsers about what kind of virtual keyboard to display.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
 */
export const TEXT_INPUT_MODES: Partial<Record<TextBoxInputType, InputMode>> = {
  0: 'text', // Text
  8: 'numeric', // Number
  9: 'email', // Email
  10: 'url', // Url
  11: 'search', // Search
  12: 'tel', // Tel
  16: 'tel', // Fax
  17: 'email' // TextOrEmail
};

/**
 * Interface for the input width
 */
export interface IInputWidth {
  isEmpty: boolean;
  maxWith: number;
  minWidth: number;
  valueLG: number;
  valueMD: number;
  valueSM: number;
  valueXS: number;
  valueXXS: number;
}

// The possible binding sources for route values
export enum RouteValueBindingSource {
  QUERY = 'Query'
}

// Generic type for a single RouteValue
export type RouteValueOfType<T extends DotNetType> = {
  /** The name of the parameter */
  parameterName: string;
  /** The type of the parameter */
  parameterType: T;
  /** The binding source of the parameter */
  bindingSource: RouteValueBindingSource;
  /** The default value of the parameter */
  defaultValue?: MapDotNetType<T>;
  /** The possible enum values of the parameter if parameterType is enum */
  enumValues?: IInputOption<MapDotNetType<T>>[];
};

// Union of all possible route values
export type IRouteValue =
  | RouteValueOfType<DotNetType.String>
  | RouteValueOfType<DotNetType.Number>
  | RouteValueOfType<DotNetType.Boolean>
  | RouteValueOfType<DotNetType.Enum>;
